import React from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { FiChevronLeft, FiChevronRight, FiRotateCcw, FiSearch } from "react-icons/fi";
import type { IDrofusInterfaceDiscipline } from "@multiconsult-group/multiplacer-core";
import PnsTreePanelContainer from "../DesignTab/routing/PnsTreePanelContainer";
import { clearSelectedPnsIdsInRedux } from "../DesignTab/routing/pnsSelectionSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  resetDrofusForekomsterFilters,
  selectDrofusForekomsterAffectedDisciplines,
  selectDrofusForekomsterFiltersOpen,
  selectDrofusForekomsterInterfaceRelevant,
  selectDrofusForekomsterNeeds,
  selectDrofusForekomsterSearch,
  selectDrofusForekomsterToBeDrawn,
  setDrofusForekomsterInterfaceRelevant,
  setDrofusForekomsterNeed,
  setDrofusForekomsterSearch,
  setDrofusForekomsterToBeDrawn,
  toggleDrofusForekomsterAffectedDiscipline,
  toggleDrofusForekomsterFiltersOpen,
} from "./drofusForekomsterSlice";

const disciplineOptions: Array<{ value: IDrofusInterfaceDiscipline; label: string; color: string }> = [
  { value: "RIE", label: "RIE", color: "#f59f00" },
  { value: "RIKT", label: "RIKT", color: "#1971c2" },
  { value: "RIAut", label: "RIAut", color: "#9c36b5" },
  { value: "RIV", label: "RIV", color: "#0c8599" },
];

const needOptions: Array<{
  key: "electrical" | "ikt" | "automation" | "vvs";
  label: string;
  description: string;
}> = [
  { key: "electrical", label: "Elektro", description: "Strømforsyning og effektbehov" },
  { key: "ikt", label: "IKT", description: "Nettverk, SD og kommunikasjon" },
  { key: "automation", label: "Automasjon", description: "Feltbus, I/O og styring" },
  { key: "vvs", label: "VVS/Gass", description: "Vann, avløp, ventilasjon, kjøling og gass" },
];

function interfaceRelevantToSegmentValue(value: boolean | undefined): string {
  if (value === true) return "true";
  if (value === false) return "false";
  return "all";
}

function segmentValueToInterfaceRelevant(value: string): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

export const DrofusForekomsterFilterBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const filtersOpen = useAppSelector(selectDrofusForekomsterFiltersOpen);
  const search = useAppSelector(selectDrofusForekomsterSearch);
  const interfaceRelevant = useAppSelector(selectDrofusForekomsterInterfaceRelevant);
  const toBeDrawn = useAppSelector(selectDrofusForekomsterToBeDrawn);
  const affectedDisciplines = useAppSelector(selectDrofusForekomsterAffectedDisciplines);
  const needs = useAppSelector(selectDrofusForekomsterNeeds);

  if (!filtersOpen) {
    return (
      <Paper withBorder radius="sm" p={4} h="100%" style={{ flexShrink: 0 }}>
        <Tooltip label="Vis filter">
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => dispatch(toggleDrofusForekomsterFiltersOpen())}
          >
            <FiChevronLeft />
          </ActionIcon>
        </Tooltip>
      </Paper>
    );
  }

  return (
    <Paper
      withBorder
      radius="sm"
      h="100%"
      w={320}
      style={{ flexShrink: 0, overflow: "hidden" }}
    >
      <Stack gap={0} h="100%">
        <Group justify="space-between" px="sm" py="xs" wrap="nowrap">
          <Box>
            <Text fw={700} size="sm">
              Filter
            </Text>
            <Text size="xs" c="dimmed">
              Serverfilter for hele kjøringen
            </Text>
          </Box>
          <Tooltip label="Skjul filter">
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => dispatch(toggleDrofusForekomsterFiltersOpen())}
            >
              <FiChevronRight />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider />

        <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
          <Stack gap="md" p="sm">
            <Stack gap={6}>
              <Text size="xs" fw={700} c="dimmed">
                SØK
              </Text>
              <TextInput
                size="xs"
                placeholder="Søk i alle indekserte felter"
                leftSection={<FiSearch size={14} />}
                value={search}
                onChange={(event) =>
                  dispatch(setDrofusForekomsterSearch(event.currentTarget.value))
                }
              />
            </Stack>

            <Stack gap={6}>
              <Text size="xs" fw={700} c="dimmed">
                PNS
              </Text>
              <Box h={320} style={{ minHeight: 0 }}>
                <PnsTreePanelContainer />
              </Box>
            </Stack>

            <Stack gap={6}>
              <Text size="xs" fw={700} c="dimmed">
                RELEVANS
              </Text>
              <SegmentedControl
                size="xs"
                fullWidth
                value={interfaceRelevantToSegmentValue(interfaceRelevant)}
                onChange={(value) =>
                  dispatch(setDrofusForekomsterInterfaceRelevant(segmentValueToInterfaceRelevant(value)))
                }
                data={[
                  { value: "all", label: "Alle" },
                  { value: "true", label: "Relevant" },
                  { value: "false", label: "Ikke relevant" },
                ]}
              />
            </Stack>

            <Stack gap={6}>
              <Text size="xs" fw={700} c="dimmed">
                SKAL MODELLERES
              </Text>
              <SegmentedControl
                size="xs"
                fullWidth
                value={interfaceRelevantToSegmentValue(toBeDrawn)}
                onChange={(value) =>
                  dispatch(setDrofusForekomsterToBeDrawn(segmentValueToInterfaceRelevant(value)))
                }
                data={[
                  { value: "all", label: "Alle" },
                  { value: "true", label: "Ja" },
                  { value: "false", label: "Nei" },
                ]}
              />
            </Stack>

            <Stack gap={8}>
              <Text size="xs" fw={700} c="dimmed">
                FAG
              </Text>
              <Group gap={6}>
                {disciplineOptions.map((option) => {
                  const active = affectedDisciplines.includes(option.value);

                  return (
                    <Badge
                      key={option.value}
                      radius="sm"
                      styles={{
                        root: {
                          backgroundColor: active ? option.color : "#ffffff",
                          border: `1px solid ${option.color}`,
                          color: active ? "#ffffff" : option.color,
                          cursor: "pointer",
                        },
                        label: {
                          color: "inherit",
                        },
                      }}
                      onClick={() => dispatch(toggleDrofusForekomsterAffectedDiscipline(option.value))}
                    >
                      {option.label}
                    </Badge>
                  );
                })}
              </Group>
            </Stack>

            <Stack gap={8}>
              <Text size="xs" fw={700} c="dimmed">
                BEHOV
              </Text>
              {needOptions.map((option) => (
                <Group key={option.key} justify="space-between" align="flex-start" wrap="nowrap">
                  <Box>
                    <Text size="sm" fw={600}>
                      {option.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {option.description}
                    </Text>
                  </Box>
                  <Switch
                    size="sm"
                    checked={needs[option.key] === true}
                    onChange={(event) =>
                      dispatch(
                        setDrofusForekomsterNeed({
                          key: option.key,
                          value: event.currentTarget.checked ? true : undefined,
                        })
                      )
                    }
                  />
                </Group>
              ))}
            </Stack>
          </Stack>
        </ScrollArea>

        <Divider />

        <Box p="sm">
          <Button
            size="xs"
            variant="light"
            radius="sm"
            fullWidth
            leftSection={<FiRotateCcw size={14} />}
            onClick={() => {
              dispatch(resetDrofusForekomsterFilters());
              dispatch(clearSelectedPnsIdsInRedux());
            }}
          >
            Tilbakestill filter
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};
